import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileBarChart2, History, Loader2, Trash2 } from 'lucide-react';
import { localeTag } from '@/i18n/format';
import { formatBytes } from '@/utils/format';
import reportService, { type ReportSummary } from '@/services/report.service';
import type { Workspace } from '@/services/workspace.service';
import { useToast } from '@/components/common/Toast';
import { useStudio } from './store';
import { REPORT_FORMAT } from './model';

/**
 * The reports saved in a workspace, under its layers: open one in the studio, open one of its earlier versions, or
 * remove it. Every save keeps a version (the server keeps the last ones); an earlier version opened and saved again
 * becomes the newest.
 */
export const WorkspaceReports: React.FC<{ workspace: Workspace }> = ({ workspace }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const openSaved = useStudio((s) => s.openSaved);
  const current = useStudio((s) => s.doc);
  const [opening, setOpening] = useState<string | null>(null);
  const [historyOf, setHistoryOf] = useState<string | null>(null);

  const { data: reports = [], isError } = useQuery({
    queryKey: ['workspaceReports', workspace.id],
    queryFn: () => reportService.list(workspace.id),
    retry: false,
  });

  const remove = useMutation({
    mutationFn: (id: string) => reportService.remove(workspace.id, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['workspaceReports', workspace.id] }),
    onError: (e: Error) => addToast(e.message, 'error'),
  });

  const open = async (r: ReportSummary, version?: number) => {
    if (current && useStudio.getState().dirty && !window.confirm(t('studio.close_unsaved'))) return;
    setOpening(r.id);
    try {
      const saved = await reportService.get(workspace.id, r.id, version);
      if (!saved.definition || typeof saved.definition !== 'object' || (saved.definition.format ?? 0) > REPORT_FORMAT) {
        throw new Error(t('studio.unknown_format'));
      }
      openSaved({ ...saved.definition, name: saved.name }, { workspace: workspace.id, id: saved.id, canUpdate: saved.canUpdate });
      if (version && saved.latest && version !== saved.latest) {
        addToast(t('studio.opened_old_version', { version, latest: saved.latest }), 'info');
      }
      setHistoryOf(null);
    } catch (e: any) {
      addToast(e?.message ?? String(e), 'error');
    } finally {
      setOpening(null);
    }
  };

  if (isError) return null;

  return (
    <div className="space-y-0.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gn-secondary pt-1">{t('studio.reports')}</div>
      {reports.length === 0 && <p className="text-[10px] text-gn-muted italic">{t('studio.no_reports')}</p>}
      {reports.map((r) => (
        <div key={r.id}>
          <div className="flex items-center gap-1 text-xs">
            <button type="button" onClick={() => open(r)} disabled={!!opening}
                    className="flex-1 min-w-0 flex items-center gap-1.5 text-left hover:text-gn-blue disabled:opacity-60"
                    title={[
                      r.updatedAt ? `${t('studio.last_saved')}: ${new Date(r.updatedAt).toLocaleString(localeTag())}${r.updatedBy ? ` · ${r.updatedBy}` : ''}` : '',
                      r.size ? `${t('studio.report_size')}: ${formatBytes(r.size)}` : '',
                    ].filter(Boolean).join('\n') || undefined}>
              {opening === r.id ? <Loader2 size={13} className="animate-spin shrink-0" />
                : <Thumbnail report={r} />}
              <span className="truncate">{r.name}</span>
            </button>
            {r.version > 0 && (
              <span className="text-[10px] text-gn-muted" title={t('studio.versions_kept', { count: r.versions })}>
                {t('studio.version_short', { version: r.version })}
              </span>
            )}
            {r.updatedAt && <span className="text-[10px] text-gn-muted">{new Date(r.updatedAt).toLocaleDateString(localeTag())}</span>}
            {r.versions > 1 && (
              <button type="button" className={`p-1 rounded hover:bg-gn-tertiary ${historyOf === r.id ? 'text-gn-blue' : 'text-gn-secondary'}`}
                      title={t('studio.versions')} aria-label={t('studio.versions')}
                      onClick={() => setHistoryOf(historyOf === r.id ? null : r.id)}>
                <History size={13} />
              </button>
            )}
            {r.canDelete && (
              <button type="button" className="p-1 rounded hover:bg-gn-tertiary text-gn-secondary" aria-label={t('studio.delete_report')}
                      onClick={() => { if (window.confirm(t('studio.delete_report_confirm', { name: r.name }))) remove.mutate(r.id); }}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
          {historyOf === r.id && <Versions workspace={workspace} report={r} disabled={!!opening} onOpen={(v) => open(r, v)} />}
        </div>
      ))}
    </div>
  );
};

/** The small picture of a report's map, read with the token; the report icon until there is one. */
const Thumbnail: React.FC<{ report: ReportSummary }> = ({ report }) => {
  const [url, setUrl] = useState<string | undefined>();
  useEffect(() => {
    let made: string | undefined;
    let alive = true;
    if (report.thumbnail) {
      reportService.thumbnail(report).then((u) => {
        if (alive) {
          made = u;
          setUrl(u);
        } else if (u) {
          URL.revokeObjectURL(u);
        }
      });
    }
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [report.thumbnail]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!url) return <FileBarChart2 size={13} className="shrink-0 text-gn-blue" />;
  return <img src={url} alt="" className="w-8 h-5 object-cover rounded-sm border border-gn-light shrink-0" />;
};

/** The versions kept of one report, the newest first: open any of them. */
const Versions: React.FC<{ workspace: Workspace; report: ReportSummary; disabled: boolean; onOpen: (version: number) => void }> =
  ({ workspace, report, disabled, onOpen }) => {
    const { t } = useTranslation();
    const { data: versions = [], isLoading, isError } = useQuery({
      queryKey: ['reportVersions', workspace.id, report.id, report.version],
      queryFn: () => reportService.versions(workspace.id, report.id),
      retry: false,
    });
    return (
      <div className="ml-5 my-1 pl-2 border-l border-gn-light space-y-0.5">
        {isLoading && <Loader2 size={12} className="animate-spin text-gn-muted" />}
        {isError && <p className="text-[10px] text-gn-danger">{t('studio.versions_failed')}</p>}
        {versions.map((v) => (
          <button key={v.version} type="button" disabled={disabled} onClick={() => onOpen(v.version)}
                  className="w-full flex items-center gap-1.5 text-left text-[10px] text-gn-secondary hover:text-gn-blue disabled:opacity-60">
            <span className="font-medium">{t('studio.version_short', { version: v.version })}</span>
            {v.latest && <span className="px-1 rounded bg-gn-surface border border-gn-light">{t('studio.latest')}</span>}
            <span className="truncate">
              {[v.savedAt ? new Date(v.savedAt).toLocaleString(localeTag()) : null, v.savedBy, v.size ? formatBytes(v.size) : null]
                .filter(Boolean).join(' · ')}
            </span>
          </button>
        ))}
      </div>
    );
  };
